import React, {useEffect, useState} from "react";
import {makeStyles} from '@material-ui/styles';
import { Container, Grid, Typography } from "@material-ui/core";
import ProductItem from "../../../products/item-horizontal";
import {useTranslation} from "tools/i18n";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import theme from "../../../theme";
import urlNormalizer from "tools/urlNormalization";
import {useRouter} from "next/router";
import { elevateClickNotification, elevateStyleWith } from "tools/elevateClient";
import Image from "next/image";
import { getCategoryBreadcrumb, getMainProduct } from "../../../../functions/productOps";
import useSessionStore from '@services/stores/SessionStore';

const useStyles = makeStyles(theme => ({
	outerWrapper: {
		display: 'flex',
		flexDirection: 'column',
		marginTop: 0,
		marginBottom: 0
	},
	wrapper: {
		marginTop: 20
	},
	title: {
		marginBottom: 10,
		fontSize: 12,
    	fontWeight: 300,
    	letterSpacing: 0.5,
    	textTransform: 'uppercase'
	},
	grid: {
        [theme.breakpoints.down('md')]: {
            width: '100%',
            margin: 0
        }
    },
	showMore: {
	    minWidth: 200,
        marginTop: 20,
        padding: '10px 24px',
        backgroundColor: theme.palette.primary.color,
        fontSize: 11,
        color: theme.palette.primary.background,
        fontWeight: 600,
        letterSpacing: 1,
        borderRadius: 0,
        '&:hover': {
	        backgroundColor: theme.palette.primary.color
        }
    },
}), {name: 'ElevateStyleWith'});

export default function ElevateStyleWith({product, configurator, breadcrumbs}) {
	const classes = useStyles();
	const router = useRouter();
	const [products, setProducts] = useState([]);
	const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const config = useSessionStore((state) => state.session.config);
	const {t} = useTranslation(['product-info']);

	useEffect(async () => {
		try {
			const searchResults = await elevateStyleWith(
				'PRD-' + getMainProduct(product, configurator).productNumber,
				config.elevateMarket,
				config.language.languageCode,
				isMobile ? 'mobile' : 'desktop'
			);

			let styleWithProducts = searchResults.recommendationLists.map((slot) => {
				let products = [];
				slot.productGroups.forEach((productGroup) => {
					products.push(productGroup.products[0]);
				});

				return products;
			});

			setProducts(styleWithProducts[0]);
		} catch (e) {
			console.log('Cant find recommendations for product with SKU: ', product.productNumber);
		}
	}, [product.productNumber]);

	async function onClick(product) {
		await elevateClickNotification(
			product.ticket,
			config.elevateMarket,
			config.language.languageCode,
			isMobile ? 'mobile' : 'desktop'
		)
	}

	function urlNormalize(url) {
		return urlNormalizer(url, router.basePath);
	}

	function getMarketingLabel(product) {
		if(!product.marketingLabel) {
			return;
		}

		return [{
			id: product.marketingLabelId,
			label: product.marketingLabel,
		}]
	}

	function getProductImage(product) {
		let productImages = product.imageInfo?.images;

		if(!productImages || productImages.length <= 0) {
			return { src: '/images/placeholder/product.jpg', width: 550, height: 510 };
		}

		let imageSource = productImages[0].sources[0];

		if(productImages.length > 1) {
			productImages.forEach((image) => {
				if (image['sources'][0].width === 550) {
					imageSource = image['sources'][0];
				}
			});
		}

		return {
			src: imageSource.url,
			width: imageSource.width || 550,
			height: imageSource.height || 510
		}
	}

	function getProductVariants(product) {
		let allVariants = product.variants.filter((variant) => variant.label).map((variant) => {
			return variant.label;
		});

		return allVariants.length > 0;
	}

	function getMainVariant(product) {
		let mainSku = product.key.split('-')[1];
		let mainVariant = product.variants.find((variant) => {
			return variant.key === mainSku;
		});

		return mainVariant || product.variants[0];
	}

	function getUUID(product) {
		return product.custom.uuid ? product.custom.uuid[0].id : 'abcdefghijkl';
	}

	function getStock(product) {
		return product.custom?.in_stock ? parseInt(product.custom.in_stock[0].label) : 0;
	}

	function getFastDelivery(product) {
		return !!product.custom?.fast_delivery_available;
	}

	function renderProduct(product, key) {
		let mainVariant = getMainVariant(product);
		let mainImage = getProductImage(product);

		return <ProductItem
			id={getUUID(mainVariant)}
			productNumber={mainVariant.key}
			name={product.title}
			manufacturer={product.brand}
			link={urlNormalize(product.link)}
			prices={{
				price: mainVariant.sellingPrice,
				listPrice: mainVariant.listPrice,
				isOnSale: mainVariant.sellingPrice < mainVariant.listPrice,
				discount: mainVariant.sellingPrice < mainVariant.listPrice ? {
					amount: mainVariant.sellingPrice - mainVariant.listPrice,
					percentage: preventFloatRoundingIssues(mainVariant.sellingPrice, mainVariant.listPrice)
				} : undefined
			}}
			image={<Image {...mainImage} alt={product.title} unoptimized/>}
			list="Elevate recommendations"
			categoryBreadcrumb={getCategoryBreadcrumb(breadcrumbs)}
			variants={getProductVariants(product) ? mainVariant.label : false}
			stock={getStock(mainVariant)}
			fastDelivery={getFastDelivery(mainVariant)}
			labels={getMarketingLabel(product)}
			available={product.inStock}
			clickCallback={onClick.bind(this, product)}
			elevateTicket={product.ticket}
			isMobile={isMobile}
			addToCart={true}
			lazyHydrate={false}
			sizes={{ lg: 12, md: 12, sm: 12, xl: 12, xs: 12 }}
			position={key + 1}
			key={key}
		/>
	}

	function preventFloatRoundingIssues(price, listPrice) {
		let centPrice = parseInt(price * 100);
		let centListPrice = parseInt(listPrice * 100);

		return parseInt(Math.round(100 * (centListPrice - centPrice) / centListPrice));
	}

	return (
		products.length > 0 ? <div className={classes.outerWrapper}>
            <div className={classes.wrapper}>
                <Container disableGutters>
                    <Typography variant="p" className={classes.title}>{t('style-with-title')}</Typography>
                    <Grid
                        container
                        justify="flex-start"
                        alignItems="stretch"
                        spacing={!isDesktop ? 1 : 2}
                        className={classes.grid}
                    >
                        {products.map((product, key) => renderProduct(product, key))}
                    </Grid>
                </Container>
            </div>
		</div> : ''
	)
}
